import React, { useCallback, useRef } from "react";

/**
 * Pulls its child a few pixels towards the pointer while the pointer is near,
 * then springs it back. Used on the primary calls to action, where the small
 * amount of pursuit makes the target feel alive under the cursor.
 *
 * Strictly a fine-pointer enhancement: on touch there is no hover to respond
 * to, and under reduced motion the offset is never written at all, so the CSS
 * falls back to a static element.
 */
function Magnetic({ children, strength = 0.32, className = "" }) {
  const ref = useRef(null);
  const frame = useRef(0);
  const next = useRef(null);

  const enabled = useCallback(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const flush = useCallback(() => {
    frame.current = 0;
    const node = ref.current;
    if (!node || !next.current) return;
    node.style.setProperty("--mw-mx", `${next.current.x}px`);
    node.style.setProperty("--mw-my", `${next.current.y}px`);
  }, []);

  const onPointerMove = useCallback(
    (event) => {
      const node = ref.current;
      if (!node || !enabled()) return;

      const rect = node.getBoundingClientRect();
      next.current = {
        x: ((event.clientX - (rect.left + rect.width / 2)) * strength).toFixed(2),
        y: ((event.clientY - (rect.top + rect.height / 2)) * strength).toFixed(2)
      };

      if (!frame.current) frame.current = requestAnimationFrame(flush);
    },
    [strength, enabled, flush]
  );

  const onPointerLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
    next.current = null;
    node.style.setProperty("--mw-mx", "0px");
    node.style.setProperty("--mw-my", "0px");
  }, []);

  return (
    <span
      ref={ref}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      className={`mw-magnetic inline-flex ${className}`}
    >
      {children}
    </span>
  );
}

export default Magnetic;
